import { useCallback, useEffect, useState } from "react";
import { ExternalLink, FileText, Loader2, RotateCcw, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import "highlight.js/styles/github-dark.min.css";

// Mesma config de sanitize usada em ChatPage pra render de mensagens —
// permite className em code/span pra que rehype-highlight aplique cores.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), ["className"]],
    span: [...(defaultSchema.attributes?.span || []), ["className"]],
  },
};

interface MarkdownPreviewPanelProps {
  url: string;
  name: string;
  onClose: () => void;
}

export function MarkdownPreviewPanel({ url, name, onClose }: MarkdownPreviewPanelProps) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const fetchMarkdown = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    setContent(null);
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      if (!signal?.aborted) setContent(text);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setError((err as Error).message || "Falha ao carregar preview");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchMarkdown(controller.signal);
    return () => controller.abort();
  }, [fetchMarkdown, reloadKey]);

  const handleReload = () => setReloadKey((k) => k + 1);
  const handleOpenNewTab = () => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <div className="flex flex-col h-full border-l border-color bg-surface">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-context border-b border-color flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <span className="text-sm font-medium text-primary truncate" title={name}>{name}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={handleOpenNewTab}
            title="Abrir em nova aba (markdown bruto)"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover text-secondary hover:text-primary transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleReload}
            title="Recarregar"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover text-secondary hover:text-primary transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover text-secondary hover:text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative bg-surface">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-tertiary">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Carregando preview…</span>
          </div>
        )}
        {!loading && error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
            <span className="text-sm text-red-400">Erro ao carregar preview: {error}</span>
            <button
              type="button"
              onClick={handleReload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-secondary hover:bg-hover text-primary transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Tentar novamente
            </button>
          </div>
        )}
        {!loading && !error && content !== null && (
          <div
            className="h-full overflow-auto px-6 py-5 prose prose-sm max-w-none break-words"
            style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeHighlight, [rehypeSanitize, sanitizeSchema]]}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarkdownPreviewPanel;
